import {
  BankOutlined,
  CloseOutlined,
  GlobalOutlined,
  InfoCircleFilled,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { InputRef } from 'antd';
import { Input, Modal, Select } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { USER_STATE_IN_ORGANIZATION } from '../../api/organization';
import { OrganizationAccessRoles, useGetUserFromAccessToken } from '../../common/auth';
import { extractDomain, isCompanyEmail, isTemporaryEmail, isValidEmail } from '../../common/utils';
import colorTokens from '../../constants/design_system/colors';
import { useGetOrganizationInvites, useGetOrganizationUsers } from '../../serverState/organization';
import { BaseButton } from '../UI/BaseButton';
import CustomTooltip from '../UI/Tooltip/CustomTooltip';

type ErrorSeverity = 'error' | 'warning';

interface ValidationError {
  message: string;
  severity: ErrorSeverity;
}

interface InviteRow {
  id: string;
  email: string;
  role: OrganizationAccessRoles;
  error: ValidationError | null;
}

function InvitationModal({
  open,
  onOk,
  onCancel,
}: {
  open: boolean;
  onOk: (invites: Array<{ email: string; role: OrganizationAccessRoles }>) => void;
  onCancel: () => void;
}) {
  const [inviteRows, setInviteRows] = useState<InviteRow[]>([
    { id: '1', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
    { id: '2', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
    { id: '3', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
    { id: '4', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
    { id: '5', email: '', role: OrganizationAccessRoles.MEMBER, error: null },
  ]);
  const firstInputRef = useRef<InputRef>(null);
  const [focusRowId, setFocusRowId] = useState<string | null>(null);
  const currentUser = useGetUserFromAccessToken();
  const orgUsersQuery = useGetOrganizationUsers();
  const queryInvites = useGetOrganizationInvites();

  // Check if current user is admin
  const currentUserEmail = currentUser.data?.user.email;
  const orgAccess = orgUsersQuery.data?.find((orgAccess) => orgAccess.userId === currentUser.data?.user.id);
  const isAdmin = orgAccess && orgAccess.roleToAssume === OrganizationAccessRoles.ADMIN;

  // Computed values
  const hasRedErrors = inviteRows.some((row) => row.error?.severity === 'error');
  // Only emails with NO errors (not even warnings) count as valid for enabling the button
  const hasAtLeastOneValidEmail = inviteRows.some((row) => row.email && row.error === null);
  // Disable if: red errors exist OR no truly valid emails (warnings don't count as valid)
  const isInviteDisabled = hasRedErrors || !hasAtLeastOneValidEmail;

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (open && firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [open]);

  // Auto-focus newly added row
  useEffect(() => {
    if (focusRowId) {
      const input = document.querySelector(`input[data-row-id="${focusRowId}"]`) as HTMLInputElement;
      if (input) {
        input.focus();
        setFocusRowId(null);
      }
    }
  }, [focusRowId, inviteRows]);

  const validateEmail = (email: string, _role: OrganizationAccessRoles): ValidationError | null => {
    if (!email) return null;

    if (!isValidEmail(email)) {
      return { message: 'Invalid email', severity: 'error' };
    }

    if (isTemporaryEmail(email)) {
      return { message: 'Emails from temporary public domains are not allowed', severity: 'error' };
    }

    // Check if already in organization (active or deactivated)
    const orgUser = orgUsersQuery.data?.find((orgAccess) => orgAccess.User.email === email);
    if (orgUser) {
      // Check if user is deactivated
      if (orgUser.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.INACTIVE) {
        return {
          message: 'Can not invite deactivated users to the organization, they can be deactivated instead',
          severity: 'warning',
        };
      }
      return { message: 'User with this email already exists in the organization', severity: 'warning' };
    }

    // Check if already has a pending invitation
    const pendingInvite = queryInvites.data?.find((invite) => invite.email === email);
    if (pendingInvite) {
      return { message: 'User with this email already exists in the organization', severity: 'warning' };
    }

    // Check for duplicates in current invitation
    const duplicates = inviteRows.filter((row) => row.email === email);
    if (duplicates.length > 1) {
      return {
        message: 'This email is already included in the current organization invitation',
        severity: 'warning',
      };
    }

    // Domain restrictions for non-admins
    const currentUserDomain = extractDomain(currentUserEmail || '');
    const inviteEmailDomain = extractDomain(email);

    if (!isAdmin && currentUserEmail && isCompanyEmail(currentUserEmail)) {
      if (currentUserDomain !== inviteEmailDomain) {
        // Check if the invited domain is another org domain (used by other org members)
        const isOtherOrgDomain = orgUsersQuery.data?.some(
          (orgUser) => extractDomain(orgUser.User.email) === inviteEmailDomain
        );

        if (isOtherOrgDomain) {
          return {
            message: 'Only organization admins can invite users with other domain email addresses',
            severity: 'error',
          };
        } else {
          return {
            message: 'Only organization admins can invite users with external domain email addresses',
            severity: 'error',
          };
        }
      }
    }

    return null;
  };

  const getEmailIcon = (email: string, error: ValidationError | null) => {
    if (error) {
      return error.severity === 'error' ? (
        <WarningOutlined className="text-red-600" />
      ) : (
        <WarningOutlined className="text-yellow-600" />
      );
    }

    if (email && isValidEmail(email)) {
      const currentUserEmail = currentUser.data?.user.email;
      const isOrgDomain =
        currentUserEmail && isCompanyEmail(currentUserEmail)
          ? extractDomain(currentUserEmail) === extractDomain(email)
          : false;

      return isOrgDomain ? (
        <BankOutlined className="text-neutral-600" />
      ) : (
        <GlobalOutlined className="text-neutral-600" />
      );
    }

    return null;
  };

  const handleEmailChange = (rowId: string, newEmail: string) => {
    setInviteRows((prevRows) =>
      prevRows.map((row) => (row.id === rowId ? { ...row, email: newEmail, error: null } : row))
    );
  };

  const handleEmailBlur = (rowId: string) => {
    setInviteRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === rowId) {
          const error = validateEmail(row.email, row.role);
          return { ...row, error };
        }
        return row;
      })
    );
  };

  const handleEmailKeyDown = (rowId: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // Prevent space from being added to input
      setInviteRows((prevRows) =>
        prevRows.map((row) => {
          if (row.id === rowId) {
            const error = validateEmail(row.email, row.role);
            return { ...row, error };
          }
          return row;
        })
      );
    }
  };

  const handleRoleChange = (rowId: string, newRole: OrganizationAccessRoles) => {
    setInviteRows((prevRows) =>
      prevRows.map((row) => {
        if (row.id === rowId) {
          const error = row.email ? validateEmail(row.email, newRole) : null;
          return { ...row, role: newRole, error };
        }
        return row;
      })
    );
  };

  const handleDeleteRow = (rowId: string) => {
    setInviteRows((prevRows) => prevRows.filter((row) => row.id !== rowId));
  };

  const handleAddRow = () => {
    const newRow: InviteRow = {
      id: Date.now().toString(),
      email: '',
      role: OrganizationAccessRoles.MEMBER,
      error: null,
    };
    setInviteRows((prevRows) => [...prevRows, newRow]);
    setFocusRowId(newRow.id);
  };

  const handleInvite = () => {
    const validInvites = inviteRows.filter(
      (row) => row.email && (row.error === null || row.error?.severity === 'warning')
    );

    if (validInvites.length === 0) return;

    onOk(
      validInvites.map((row) => ({
        email: row.email,
        role: row.role,
      }))
    );
  };

  return (
    <Modal
      title={<span className="text-neutral-1000 text-base font-semibold">Add teammates to your Organization</span>}
      open={open}
      onCancel={onCancel}
      width={'640px'}
      footer={null}
      centered
    >
      <div className="flex flex-col gap-5 pt-1 h-[512px] justify-between">
        {/* Row list - scrollable */}
        <div className="flex flex-col gap-2 overflow-y-auto flex-1 min-h-0 pr-0.5">
          {inviteRows.map((row, index) => (
            <div key={row.id} className="flex flex-col gap-[2px]">
              <div className="flex gap-3 items-center">
                {/* Email input with icon */}
                <div className="flex-1 relative">
                  <Input
                    ref={index === 0 ? firstInputRef : null}
                    className="h-10 border-neutral-500 text-neutral-800 text-base font-normal pr-10"
                    placeholder="Enter email"
                    value={row.email}
                    onChange={(e) => handleEmailChange(row.id, e.target.value)}
                    onBlur={() => handleEmailBlur(row.id)}
                    onKeyDown={(e) => handleEmailKeyDown(row.id, e)}
                    status={row.error?.severity === 'error' ? 'error' : ''}
                    size="large"
                    data-row-id={row.id}
                    autoComplete="off"
                  />
                  {getEmailIcon(row.email, row.error) && (
                    <CustomTooltip
                      title={
                        row.error?.message ||
                        (row.email && isValidEmail(row.email)
                          ? isCompanyEmail(currentUser.data?.user.email || '') &&
                            extractDomain(currentUser.data?.user.email || '') === extractDomain(row.email)
                            ? 'Organization domain email'
                            : 'External domain email'
                          : '')
                      }
                    >
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 cursor-default">
                        {getEmailIcon(row.email, row.error)}
                      </div>
                    </CustomTooltip>
                  )}
                </div>

                {/* Role selector */}
                <CustomTooltip title={!isAdmin ? 'Only organization admins can change organization roles' : ''}>
                  <div className={!isAdmin ? 'cursor-not-allowed' : ''}>
                    <Select
                      className="h-8 w-[90px] ml-1"
                      value={row.role}
                      onChange={(value) => handleRoleChange(row.id, value)}
                      size="small"
                      variant="borderless"
                      disabled={!isAdmin}
                    >
                      <Select.Option value={OrganizationAccessRoles.MEMBER}>Member</Select.Option>
                      <Select.Option value={OrganizationAccessRoles.ADMIN}>Admin</Select.Option>
                    </Select>
                  </div>
                </CustomTooltip>

                {/* Delete button */}
                <CustomTooltip title={inviteRows.length === 1 ? 'At least one field is required' : 'Remove email'}>
                  <CloseOutlined
                    className={`flex items-center justify-center w-5 h-5 p-1 rounded-sm ${
                      inviteRows.length === 1
                        ? 'text-neutral-500 cursor-not-allowed'
                        : 'text-neutral-900 cursor-pointer hover:bg-neutral-200'
                    }`}
                    onClick={() => inviteRows.length > 1 && handleDeleteRow(row.id)}
                  />
                </CustomTooltip>
              </div>
            </div>
          ))}

          {/* Add row button */}
          <div className="flex justify-start mb-4">
            <BaseButton type="secondary" onClick={handleAddRow} size="medium">
              <PlusOutlined className="text-base" />
              Add Another Member
            </BaseButton>
          </div>
        </div>

        {/* Bottom section */}
        <div className="flex justify-between items-center">
          <div className="flex space-x-2">
            <InfoCircleFilled style={{ color: colorTokens.neutral[800] }} />
            <div className="w-80 text-neutral-1000 text-xs font-normal font-['Figtree'] leading-none">
              Only organization admins can invite users with external domain email addresses.
            </div>
          </div>

          <BaseButton type="primary" size="large" onClick={handleInvite} disabled={isInviteDisabled}>
            Invite to Organization
          </BaseButton>
        </div>
      </div>
    </Modal>
  );
}

export default InvitationModal;
